<!-- FieldSupervisorDashboard.vue - Dashboard principal del supervisor de campo -->
<template>
  <div class="field-supervisor-dashboard">
    <!-- Header con información del supervisor -->
    <div class="supervisor-header mb-6">
      <v-card variant="flat" color="primary" class="text-white">
        <v-card-text>
          <div class="d-flex align-center">
            <v-avatar size="64" class="mr-4" color="white">
              <v-icon size="32" color="primary">mdi-account-hard-hat</v-icon>
            </v-avatar>
            
            <div class="flex-grow-1">
              <h1 class="text-h4 mb-2">Dashboard - Supervisor de Campo</h1>
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-account</v-icon>
                <span class="text-h6 mr-4">{{ supervisorInfo.name }}</span>
                <v-icon class="mr-2">mdi-map-marker</v-icon>
                <span class="text-body-1 mr-4">{{ supervisorInfo.zone }}</span>
                <v-icon class="mr-2">mdi-clock</v-icon>
                <span class="text-body-1">{{ formatTime(currentTime) }}</span>
              </div>
              <div class="mt-2">
                <v-chip
                  :color="getShiftStatusColor(supervisorInfo.shift_status)"
                  size="small"
                  prepend-icon="mdi-clock-outline"
                >
                  {{ supervisorInfo.shift_status }} - {{ supervisorInfo.shift_hours }}
                </v-chip>
                <v-chip
                  color="white"
                  text-color="primary"
                  size="small"
                  class="ml-2"
                  prepend-icon="mdi-account-group"
                >
                  {{ activeOperations.length }} operaciones activas
                </v-chip>
              </div>
            </div>

            <!-- Estado de conectividad -->
            <div class="text-right">
              <div class="connectivity-status mb-2">
                <v-chip
                  :color="connectionStatus.color"
                  size="small"
                  :prepend-icon="connectionStatus.icon"
                >
                  {{ connectionStatus.text }}
                </v-chip>
              </div>
              <div class="weather-info">
                <v-chip
                  color="white"
                  text-color="primary"
                  size="small"
                  prepend-icon="mdi-weather-partly-cloudy"
                >
                  {{ weatherInfo.temperature }}°C - {{ weatherInfo.condition }}
                </v-chip>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Navegación por pestañas -->
    <v-card class="mb-4" variant="outlined">
      <v-tabs
        v-model="activeTab"
        bg-color="transparent"
        color="primary"
        grow
      >
        <v-tab value="operations" prepend-icon="mdi-clipboard-list">
          Operaciones
          <v-badge
            v-if="pendingOperations.length > 0"
            :content="pendingOperations.length"
            color="error"
            inline
            class="ml-2"
          ></v-badge>
        </v-tab>
        
        <v-tab value="teams" prepend-icon="mdi-account-group">
          Equipos
          <v-badge
            v-if="availableTeams > 0"
            :content="availableTeams"
            color="success"
            inline
            class="ml-2"
          ></v-badge>
        </v-tab>
        
        <v-tab value="resources" prepend-icon="mdi-package-variant">
          Recursos
          <v-badge
            v-if="lowStockAlerts > 0"
            :content="lowStockAlerts"
            color="warning"
            inline
            class="ml-2"
          ></v-badge>
        </v-tab>
        
        <v-tab value="inventory" prepend-icon="mdi-clipboard-list-outline">
          Inventario
        </v-tab>
        
        <v-tab value="expenses" prepend-icon="mdi-cash-multiple">
          Gastos
          <v-badge
            v-if="pendingExpensesCount > 0"
            :content="pendingExpensesCount"
            color="warning"
            inline
            class="ml-2"
          ></v-badge>
        </v-tab>
        
        <v-tab value="communications" prepend-icon="mdi-message">
          Comunicaciones
          <v-badge
            v-if="unreadMessages > 0"
            :content="unreadMessages"
            color="info"
            inline
            class="ml-2"
          ></v-badge>
        </v-tab>
        
        <v-tab value="performance" prepend-icon="mdi-chart-line">
          Rendimiento
        </v-tab>
        
        <v-tab value="tracking" prepend-icon="mdi-map-marker-radius">
          Tracking
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- Contenido de las pestañas -->
    <v-window v-model="activeTab" class="tab-content">
      <!-- Panel de operaciones -->
      <v-window-item value="operations">
        <FieldOperationsPanel
          :operations="fieldOperations"
          :teams="fieldTeams"
          @start-operation="handleStartOperation"
          @pause-operation="handlePauseOperation"
          @complete-operation="handleCompleteOperation"
          @assign-team="handleAssignTeam"
        />
      </v-window-item>

      <!-- Panel de equipos -->
      <v-window-item value="teams">
        <FieldTeamsPanel
          :teams="fieldTeams"
          :assignments="teamAssignments"
          @assign-team="handleAssignTeamToOperation"
          @reassign-team="handleReassignTeam"
          @emergency-support="handleEmergencySupport"
        />
      </v-window-item>

      <!-- Panel de recursos -->
      <v-window-item value="resources">
        <FieldResourcesPanel
          :resources="fieldResources"
          @request-resource="handleRequestResource"
          @transfer-resource="handleTransferResource"
          @restock-resource="handleRestockResource"
          @emergency-restock="handleEmergencyRestock"
        />
      </v-window-item>

      <!-- Panel de Inventario (Solicitudes) -->
      <v-window-item value="inventory">
        <FieldInventoryRequestPanel
          :agencies="assignedAgencies"
          @request-created="handleInventoryRequestCreated"
        />
      </v-window-item>

      <!-- Panel de Gastos -->
      <v-window-item value="expenses">
        <v-tabs v-model="expensesSubTab" bg-color="grey-lighten-4" class="mb-4">
          <v-tab value="fixed">
            <v-icon start>mdi-home-city</v-icon>
            Gastos Fijos
          </v-tab>
          <v-tab value="variable">
            <v-icon start>mdi-cash-fast</v-icon>
            Gastos Variables
          </v-tab>
          <v-tab value="reports">
            <v-icon start>mdi-chart-box</v-icon>
            Reportes
          </v-tab>
        </v-tabs>
        
        <v-window v-model="expensesSubTab">
          <v-window-item value="fixed">
            <FieldFixedExpensesPanel
              :agencies="assignedAgencies"
            />
          </v-window-item>
          <v-window-item value="variable">
            <FieldVariableExpensesPanel
              :agencies="assignedAgencies"
              :fondo-disponible="fondoDisponible"
              @expense-created="handleVariableExpenseCreated"
              @refresh-fondo="loadFondoDisponible"
            />
          </v-window-item>
          <v-window-item value="reports">
            <FieldExpenseReportsPanel />
          </v-window-item>
        </v-window>
      </v-window-item>

      <!-- Panel de comunicaciones -->
      <v-window-item value="communications">
        <FieldCommunicationsPanel
          :messages="fieldMessages"
          :alerts="fieldAlerts"
          :contacts="fieldContacts"
          @send-message="handleSendMessage"
          @acknowledge-alert="handleAcknowledgeAlert"
          @resolve-alert="handleResolveAlert"
          @escalate-alert="handleEscalateAlert"
          @emergency-broadcast="handleEmergencyBroadcast"
        />
      </v-window-item>

      <!-- Panel de rendimiento -->
      <v-window-item value="performance">
        <FieldPerformanceDashboard
          :performance-data="performanceData"
          @export-report="handleExportReport"
          @refresh-data="handleRefreshData"
        />
      </v-window-item>

      <!-- Panel de Tracking de Operadores -->
      <v-window-item value="tracking">
        <OperatorTrackingMap map-height="550px" />
      </v-window-item>
    </v-window>

    <!-- Panel de emergencia flotante -->
    <v-fab
      v-if="emergencyMode"
      color="error"
      icon="mdi-alarm-light"
      location="bottom end"
      size="large"
      class="emergency-fab"
      @click="handleEmergencyMode"
    >
      <v-tooltip activator="parent" location="left">
        Modo Emergencia Activo
      </v-tooltip>
    </v-fab>

    <!-- Notificaciones en tiempo real -->
    <v-snackbar
      v-model="notification.show"
      :color="notification.color"
      :timeout="notification.timeout"
      location="top right"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{ notification.icon }}</v-icon>
        {{ notification.message }}
      </div>
      
      <template v-slot:actions>
        <v-btn
          icon="mdi-close"
          size="small"
          @click="notification.show = false"
        ></v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import FieldOperationsPanel from './FieldOperationsPanel.vue'
import FieldTeamsPanel from './FieldTeamsPanel.vue'
import FieldResourcesPanel from './FieldResourcesPanel.vue'
import FieldCommunicationsPanel from './FieldCommunicationsPanel.vue'
import FieldPerformanceDashboard from './FieldPerformanceDashboard.vue'
import FieldInventoryRequestPanel from './FieldInventoryRequestPanel.vue'
import FieldFixedExpensesPanel from './FieldFixedExpensesPanel.vue'
import FieldVariableExpensesPanel from './FieldVariableExpensesPanel.vue'
import FieldExpenseReportsPanel from './FieldExpenseReportsPanel.vue'
import OperatorTrackingMap from './OperatorTrackingMap.vue'
import { FieldSupervisorService } from '@/services/roles/FieldSupervisorService'
import ApiClient from '@/services/api/api-client'

// Props
const props = defineProps({
  supervisorData: {
    type: Object,
    default: () => ({})
  }
})

// Reactive data
const activeTab = ref('operations')
const expensesSubTab = ref('fixed')
const fondoDisponible = ref(0)
const currentTime = ref(new Date())
const emergencyMode = ref(false)
let timeInterval = null

// Notification system
const notification = ref({
  show: false,
  message: '',
  color: 'info',
  icon: 'mdi-information',
  timeout: 5000
})

// Service instance
const fieldService = new FieldSupervisorService()

// Supervisor info
const supervisorInfo = ref({
  name: 'Carlos Mendoza',
  zone: 'Zona Norte - Sector A',
  shift_status: 'Turno Diurno',
  shift_hours: '06:00 - 18:00'
})

// Connection and weather info
const connectionStatus = ref({
  color: 'success',
  icon: 'mdi-wifi',
  text: 'Conectado'
})

const weatherInfo = ref({
  temperature: 24,
  condition: 'Parcialmente nublado'
})

// Data from service
const fieldOperations = ref([])
const fieldTeams = ref([])
const fieldResources = ref([])
const fieldMessages = ref([])
const fieldAlerts = ref([])
const fieldContacts = ref([])
const teamAssignments = ref([])
const performanceData = ref({})
const assignedAgencies = ref([])
const pendingExpensesCount = ref(0)

// Computed properties
const activeOperations = computed(() =>
  fieldOperations.value.filter(op => op.status === 'in_progress')
)

const pendingOperations = computed(() =>
  fieldOperations.value.filter(op => op.status === 'pending')
)

const availableTeams = computed(() =>
  fieldTeams.value.filter(team => team.status === 'available').length
)

const lowStockAlerts = computed(() =>
  fieldResources.value.filter(resource => 
    resource.current_quantity <= resource.min_quantity
  ).length
)

const unreadMessages = computed(() =>
  fieldMessages.value.filter(msg => !msg.read).length
)

// Lifecycle
onMounted(async () => {
  await initializeDashboard()
  setupRealTimeUpdates()
})

onUnmounted(() => {
  if (timeInterval) {
    clearInterval(timeInterval)
  }
})

// Methods
const initializeDashboard = async () => {
  try {
    // Cargar datos iniciales
    const [operations, teams, resources, messages, alerts, contacts] = await Promise.all([
      fieldService.getFieldOperations(),
      fieldService.getWorkTeams(),
      fieldService.getFieldResources(),
      fieldService.getMessages(),
      fieldService.getAlerts(),
      fieldService.getContacts()
    ])

    fieldOperations.value = operations
    fieldTeams.value = teams
    fieldResources.value = resources
    fieldMessages.value = messages
    fieldAlerts.value = alerts
    fieldContacts.value = contacts

    // Cargar agencias asignadas para inventario/gastos
    await loadAssignedAgencies()
    await loadPendingExpenses()
    await loadFondoDisponible()

    showNotification('Dashboard inicializado correctamente', 'success', 'mdi-check-circle')
  } catch (error) {
    console.error('Error al inicializar dashboard:', error)
    showNotification('Error al cargar datos del dashboard', 'error', 'mdi-alert-circle')
  }
}

const setupRealTimeUpdates = () => {
  // Actualizar hora cada minuto
  timeInterval = setInterval(() => {
    currentTime.value = new Date()
  }, 60000)

  // Simular actualizaciones en tiempo real
  setInterval(() => {
    checkForUpdates()
  }, 30000)
}

// Cargar agencias asignadas al supervisor
const loadAssignedAgencies = async () => {
  try {
    const response = await ApiClient.get('/field-supervisor/assigned-agencies')
    if (response.data?.success) {
      assignedAgencies.value = response.data.data?.agencies || []
    }
  } catch (error) {
    console.error('Error cargando agencias:', error)
    assignedAgencies.value = []
  }
}

// Cargar contador de gastos fijos pendientes
const loadPendingExpenses = async () => {
  try {
    const response = await ApiClient.get('/field-supervisor/fixed-expenses/pending')
    if (response.data?.success) {
      pendingExpensesCount.value = response.data.data?.pending?.length || 0
    }
  } catch (error) {
    console.error('Error cargando gastos pendientes:', error)
    pendingExpensesCount.value = 0
  }
}

// Cargar fondo disponible del grupo
const loadFondoDisponible = async () => {
  try {
    const response = await ApiClient.get('/field-supervisor/group-info')
    // ApiClient desencapsula, así que response ya es el data
    const data = response.data || response
    fondoDisponible.value = parseFloat(data.fondo_disponible || data.group?.fondo_disponible || 0)
  } catch (error) {
    console.error('Error cargando fondo disponible:', error)
    fondoDisponible.value = 0
  }
}

// Handler para gastos variables creados
const handleVariableExpenseCreated = (expense) => {
  showNotification(`Gasto "${expense?.concepto || 'variable'}" registrado correctamente`, 'success', 'mdi-check-circle')
  loadFondoDisponible()
}

// Handler para solicitudes de inventario creadas
const handleInventoryRequestCreated = (data) => {
  showNotification(`Solicitud #${data.request_id} creada exitosamente`, 'success', 'mdi-check-circle')
}

const checkForUpdates = async () => {
  try {
    // Simular comprobación de actualizaciones
    const hasUpdates = Math.random() > 0.7

    if (hasUpdates) {
      showNotification('Nuevas actualizaciones disponibles', 'info', 'mdi-refresh')
    }
  } catch (error) {
    console.error('Error al verificar actualizaciones:', error)
  }
}

// Utility methods
const formatTime = (date) => {
  return date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getShiftStatusColor = (status) => {
  const colors = {
    'Turno Diurno': 'white',
    'Turno Nocturno': 'blue-grey-lighten-1',
    'Turno Especial': 'amber'
  }
  return colors[status] || 'white'
}

const showNotification = (message, color = 'info', icon = 'mdi-information') => {
  notification.value = {
    show: true,
    message,
    color,
    icon,
    timeout: 5000
  }
}

// Event handlers para operaciones
const handleStartOperation = async (operationId) => {
  try {
    await fieldService.startFieldOperation(operationId)
    showNotification('Operación iniciada exitosamente', 'success', 'mdi-play-circle')
    await loadOperations()
  } catch (error) {
    showNotification('Error al iniciar operación', 'error', 'mdi-alert-circle')
  }
}

const handlePauseOperation = async (operationId) => {
  try {
    await fieldService.pauseFieldOperation(operationId)
    showNotification('Operación pausada', 'warning', 'mdi-pause-circle')
    await loadOperations()
  } catch (error) {
    showNotification('Error al pausar operación', 'error', 'mdi-alert-circle')
  }
}

const handleCompleteOperation = async (operationId) => {
  try {
    await fieldService.completeFieldOperation(operationId)
    showNotification('Operación completada exitosamente', 'success', 'mdi-check-circle')
    await loadOperations()
  } catch (error) {
    showNotification('Error al completar operación', 'error', 'mdi-alert-circle')
  }
}

const handleAssignTeam = async (data) => {
  try {
    await fieldService.assignWorkTeam(data.operationId, data.teamId)
    showNotification('Equipo asignado correctamente', 'success', 'mdi-account-check')
    await loadTeams()
  } catch (error) {
    showNotification('Error al asignar equipo', 'error', 'mdi-alert-circle')
  }
}

// Event handlers para equipos
const handleAssignTeamToOperation = async (teamId) => {
  try {
    // Lógica para asignar equipo a operación
    showNotification('Equipo asignado a operación', 'success', 'mdi-account-arrow-right')
  } catch (error) {
    showNotification('Error al asignar equipo', 'error', 'mdi-alert-circle')
  }
}

const handleReassignTeam = async (teamId) => {
  try {
    await fieldService.reassignWorkTeam(teamId)
    showNotification('Equipo reasignado exitosamente', 'info', 'mdi-account-switch')
    await loadTeams()
  } catch (error) {
    showNotification('Error al reasignar equipo', 'error', 'mdi-alert-circle')
  }
}

const handleEmergencySupport = async () => {
  try {
    await fieldService.requestEmergencySupport()
    emergencyMode.value = true
    showNotification('Solicitud de apoyo de emergencia enviada', 'error', 'mdi-alarm-light')
  } catch (error) {
    showNotification('Error al solicitar apoyo de emergencia', 'error', 'mdi-alert-circle')
  }
}

// Event handlers para recursos
const handleRequestResource = async (resourceId) => {
  try {
    await fieldService.requestFieldResource(resourceId)
    showNotification('Recurso solicitado correctamente', 'success', 'mdi-package-down')
    await loadResources()
  } catch (error) {
    showNotification('Error al solicitar recurso', 'error', 'mdi-alert-circle')
  }
}

const handleTransferResource = async (resourceId) => {
  try {
    await fieldService.transferFieldResource(resourceId)
    showNotification('Transferencia de recurso iniciada', 'info', 'mdi-swap-horizontal')
    await loadResources()
  } catch (error) {
    showNotification('Error al transferir recurso', 'error', 'mdi-alert-circle')
  }
}

const handleRestockResource = async (resourceId) => {
  try {
    await fieldService.restockFieldResource(resourceId)
    showNotification('Solicitud de reabastecimiento enviada', 'warning', 'mdi-package-up')
    await loadResources()
  } catch (error) {
    showNotification('Error al reabastecer recurso', 'error', 'mdi-alert-circle')
  }
}

const handleEmergencyRestock = async () => {
  try {
    await fieldService.emergencyResourceRestock()
    showNotification('Reabastecimiento de emergencia solicitado', 'error', 'mdi-truck-fast')
  } catch (error) {
    showNotification('Error en reabastecimiento de emergencia', 'error', 'mdi-alert-circle')
  }
}

// Event handlers para comunicaciones
const handleSendMessage = async (contactId) => {
  try {
    await fieldService.sendFieldMessage(contactId, 'Mensaje desde dashboard')
    showNotification('Mensaje enviado exitosamente', 'success', 'mdi-send')
  } catch (error) {
    showNotification('Error al enviar mensaje', 'error', 'mdi-alert-circle')
  }
}

const handleAcknowledgeAlert = async (alertId) => {
  try {
    await fieldService.acknowledgeAlert(alertId)
    showNotification('Alerta reconocida', 'info', 'mdi-check')
    await loadAlerts()
  } catch (error) {
    showNotification('Error al reconocer alerta', 'error', 'mdi-alert-circle')
  }
}

const handleResolveAlert = async (alertId) => {
  try {
    await fieldService.resolveFieldAlert(alertId)
    showNotification('Alerta resuelta exitosamente', 'success', 'mdi-check-all')
    await loadAlerts()
  } catch (error) {
    showNotification('Error al resolver alerta', 'error', 'mdi-alert-circle')
  }
}

const handleEscalateAlert = async (alertId) => {
  try {
    await fieldService.escalateAlert(alertId)
    showNotification('Alerta escalada a supervisión', 'warning', 'mdi-arrow-up-bold')
    await loadAlerts()
  } catch (error) {
    showNotification('Error al escalar alerta', 'error', 'mdi-alert-circle')
  }
}

const handleEmergencyBroadcast = async () => {
  try {
    await fieldService.sendEmergencyAlert('Difusión de emergencia desde campo')
    emergencyMode.value = true
    showNotification('Difusión de emergencia enviada', 'error', 'mdi-alarm-light')
  } catch (error) {
    showNotification('Error en difusión de emergencia', 'error', 'mdi-alert-circle')
  }
}

// Event handlers para rendimiento
const handleExportReport = async () => {
  try {
    await fieldService.exportFieldReport()
    showNotification('Reporte exportado exitosamente', 'success', 'mdi-download')
  } catch (error) {
    showNotification('Error al exportar reporte', 'error', 'mdi-alert-circle')
  }
}

const handleRefreshData = async () => {
  showNotification('Actualizando datos...', 'info', 'mdi-refresh')
  await initializeDashboard()
}

const handleEmergencyMode = () => {
  emergencyMode.value = false
  showNotification('Modo emergencia desactivado', 'info', 'mdi-check-circle')
}

// Data loading methods
const loadOperations = async () => {
  try {
    fieldOperations.value = await fieldService.getFieldOperations()
  } catch (error) {
    console.error('Error al cargar operaciones:', error)
  }
}

const loadTeams = async () => {
  try {
    fieldTeams.value = await fieldService.getWorkTeams()
  } catch (error) {
    console.error('Error al cargar equipos:', error)
  }
}

const loadResources = async () => {
  try {
    fieldResources.value = await fieldService.getFieldResources()
  } catch (error) {
    console.error('Error al cargar recursos:', error)
  }
}

const loadAlerts = async () => {
  try {
    fieldAlerts.value = await fieldService.getAlerts()
  } catch (error) {
    console.error('Error al cargar alertas:', error)
  }
}
</script>

<style scoped>
.field-supervisor-dashboard {
  min-height: 100vh;
  padding: 24px;
  background-color: #f5f5f5;
}

.supervisor-header {
  margin-bottom: 24px;
}

.tab-content {
  min-height: 600px;
}

.emergency-fab {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.connectivity-status,
.weather-info {
  margin-bottom: 8px;
}

/* Responsive design */
@media (max-width: 768px) {
  .field-supervisor-dashboard {
    padding: 16px;
  }
  
  .supervisor-header .text-h4 {
    font-size: 1.5rem !important;
  }
  
  .tab-content {
    min-height: 500px;
  }
}
</style>
